/**
 * #1806 — read what Google actually said before telling her it worked.
 *
 * `google-calendar-auth`'s `add_events` posts each event to Google one at a
 * time and answers HTTP 200 with a per-event ledger:
 *
 *   { success: true, events: [{ title, success, id, error }, …], total_added }
 *
 * The 200 is for the BATCH — it means "I ran your list", not "Google took
 * them". With a revoked token every row comes back `success: false`,
 * `id: null`, `error: "Invalid Credentials"`, and the status is still 200.
 * Until this file existed SchedulePreview read only the ids and appended
 * " + Google Calendar" to the toast regardless, so a dead token produced a
 * confirmation over nothing (house rule 16). These helpers are the read.
 *
 * Pure on purpose: the component cannot be mounted in vitest, so the decision
 * — what to append, what to say instead, whether the token is dead — lives
 * here where a test can feed it the exact shape the server returns.
 */

export interface GooglePushSummary {
  /** rows Google accepted — `success === true` AND an event id came back.
   *  An "accepted" row with no id cannot be written back, so the next import
   *  could not dedup against it and would push the same event again; we
   *  cannot prove it landed, so it is not counted as landed. */
  ok: number;
  /** rows Google refused, rows we could not read, and accepted rows with no id */
  failed: number;
  /** order-matched to the server's array; a string exactly where `ok` counted
   *  the row, null everywhere else */
  ids: Array<string | null>;
  kind: "all" | "partial" | "none";
  /** at least one refusal reads as an auth problem — the token is dead, not
   *  one event malformed */
  authFailure: boolean;
}

/** What Google's error text looks like when the token itself is the problem.
 *  `Invalid Credentials` is the live 401 body; `invalid_grant` is the refresh
 *  endpoint's wording when the refresh token has been revoked. */
const AUTH_REFUSAL = /\b401\b|invalid_grant|invalid credentials|unauthori[sz]ed|invalid authentication credentials/i;

function readId(row: Record<string, unknown>): string | null {
  const candidate = row.google_event_id ?? row.id;
  return typeof candidate === "string" && candidate.length > 0 ? candidate : null;
}

/**
 * Fold the server's `events` ledger into counts. Anything that is not an
 * array of rows — `undefined`, a string, an object — summarises as `none`
 * with no ids: a shape we could not read is never a success.
 *
 * `attempted` is how many events we SENT. Rows are matched to requests by
 * position, so a ledger LONGER than the request is malformed — row i may
 * not describe event i — and is likewise `none` with no ids: nothing gets
 * written back off a ledger we cannot trust the order of. A ledger shorter
 * than the request is fine here; the missing tail is counted as not-made-it
 * in `googlePushOutcome`.
 */
export function summarizeGooglePush(events: unknown, attempted?: number): GooglePushSummary {
  const empty: GooglePushSummary = { ok: 0, failed: 0, ids: [], kind: "none", authFailure: false };
  if (!Array.isArray(events)) return empty;
  if (attempted !== undefined && events.length > attempted) return empty;

  let ok = 0;
  let failed = 0;
  let authFailure = false;
  const ids: Array<string | null> = events.map((raw) => {
    if (typeof raw !== "object" || raw === null) {
      failed += 1;
      return null;
    }
    const row = raw as Record<string, unknown>;
    const id = row.success === true ? readId(row) : null;
    if (id !== null) {
      ok += 1;
      return id;
    }
    failed += 1;
    if (typeof row.error === "string" && AUTH_REFUSAL.test(row.error)) authFailure = true;
    return null;
  });

  const kind: GooglePushSummary["kind"] = ok === 0 ? "none" : failed === 0 ? "all" : "partial";
  return { ok, failed, ids, kind, authFailure };
}

export const GOOGLE_SYNC_FAILED_TOAST = "Saved to Kin 💜 but Google Calendar sync failed. Try again later.";
export const GOOGLE_DISCONNECTED_TOAST = "Google Calendar disconnected. Reconnect in settings.";
export const GOOGLE_ALL_SUFFIX = " + Google Calendar";

export interface GooglePushOutcome {
  /** Appended to the "Saved to Kin" toast. null means: do NOT show the
   *  success toast at all — `toast` carries what to say instead. */
  suffix: string | null;
  /** A toast of its own. With `suffix === null` it replaces the success
   *  toast (nothing reached Google). With a suffix it is shown AS WELL — a
   *  mixed ledger where the token died mid-batch still needs "reconnect",
   *  or Settings flips to Not connected with no word about why. */
  toast: string | null;
  /** The refusal was an auth refusal — the token is dead. The caller runs the
   *  real disconnect (which deletes the token row) rather than flipping a
   *  flag: `check_status` reports connected whenever the row exists, so a
   *  flag alone would read Not connected only until the next app resume. */
  markDisconnected: boolean;
}

/**
 * Turn a summary into what the screen says.
 *
 * `attempted` is how many events we SENT and is always the denominator.
 * Google's ledger is matched by order, so a ledger shorter than the request
 * means some events were never even answered — those count as not-made-it,
 * never as silently fine. A summary that somehow counts MORE rows than were
 * sent is malformed and reads as nothing-landed, the same as an unreadable
 * response (summarizeGooglePush already refuses such a ledger when told
 * `attempted`; this is the belt to that brace).
 */
export function googlePushOutcome(summary: GooglePushSummary, attempted: number): GooglePushOutcome {
  const total = attempted;
  if (summary.ok === 0 || summary.ok + summary.failed > attempted) {
    return {
      suffix: null,
      toast: summary.authFailure ? GOOGLE_DISCONNECTED_TOAST : GOOGLE_SYNC_FAILED_TOAST,
      markDisconnected: summary.authFailure,
    };
  }
  if (summary.ok >= total) {
    return { suffix: GOOGLE_ALL_SUFFIX, toast: null, markDisconnected: false };
  }
  return {
    suffix: ` + ${summary.ok} of ${total} to Google Calendar — the rest didn't make it`,
    // The token can die partway through the sequential batch: some events
    // landed, the rest were refused as unauthenticated. She still needs to
    // hear "reconnect", not just "N of M".
    toast: summary.authFailure ? GOOGLE_DISCONNECTED_TOAST : null,
    markDisconnected: summary.authFailure,
  };
}
